import { Component, inject } from '@angular/core';

import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { FilePageLinkSource } from '../../../../shared/file-page-link.service';
import { FilePageScopeComponent } from '../../../../shared/ui/file-page-scope/file-page-scope.component';

export interface OverlapResolutionData {
  overlappingPages: number[];
  newPages: number[];
  filename: string;
  projectId: string;
  fileId: string;
}

export interface OverlapResolutionResult {
  action: 'skip' | 'force-reprocess' | 'cancel';
}

/** Dialog for resolving overlapping page assignments when plan pages cover the same building-code sections. */
@Component({
  selector: 'app-overlap-resolution-dialog',
  imports: [FormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatRadioModule, FilePageScopeComponent],
  templateUrl: './overlap-resolution-dialog.component.html',
  styleUrls: ['./overlap-resolution-dialog.component.scss'],
})
export class OverlapResolutionDialogComponent {
  dialogRef = inject<MatDialogRef<OverlapResolutionDialogComponent>>(MatDialogRef);
  data = inject<OverlapResolutionData>(MAT_DIALOG_DATA);

  readonly scopeSource: FilePageLinkSource = {
    projectId: this.data.projectId,
    rawFileId: this.data.fileId,
    rawPageId: this.data.overlappingPages[0] ?? null,
    filename: this.data.filename,
  };

  readonly overlappingPagesText = this.getOverlappingPagesText();

  selectedAction: 'skip' | 'force-reprocess' = 'skip';

  /**
   * Formats the list of overlapping pages for display
   */
  getOverlappingPagesText(): string {
    if (this.data.overlappingPages.length === 0) {
      return '';
    }

    // Group consecutive pages into ranges
    const ranges: string[] = [];
    let start = this.data.overlappingPages[0];
    let end = start;

    for (let i = 1; i < this.data.overlappingPages.length; i++) {
      const current = this.data.overlappingPages[i];
      if (current === end + 1) {
        end = current;
      } else {
        // Add the previous range
        if (start === end) {
          ranges.push(start.toString());
        } else {
          ranges.push(`${start}-${end}`);
        }
        start = current;
        end = current;
      }
    }

    // Add the final range
    if (start === end) {
      ranges.push(start.toString());
    } else {
      ranges.push(`${start}-${end}`);
    }

    return ranges.join(', ');
  }

  /**
   * Gets the count of new pages that would be processed
   */
  getNewPagesCount(): number {
    return this.data.newPages.length;
  }

  /**
   * Gets the total count of pages that would be processed
   */
  getTotalPagesCount(): number {
    return this.data.overlappingPages.length + this.data.newPages.length;
  }

  /**
   * Gets the count of overlapping pages
   */
  getOverlappingPagesCount(): number {
    return this.data.overlappingPages.length;
  }

  /**
   * Handles the continue button click
   */
  onContinue(): void {
    const result: OverlapResolutionResult = {
      action: this.selectedAction,
    };
    this.dialogRef.close(result);
  }

  /**
   * Handles the cancel button click
   */
  onCancel(): void {
    const result: OverlapResolutionResult = {
      action: 'cancel',
    };
    this.dialogRef.close(result);
  }
}
